<script setup lang="ts">
import { inject, onMounted, type Component } from 'vue';
import { CdrText, CdrLink, IconCaretRight } from '@rei/cedar';

import { useAnalytics } from '../../../common/composables/useAnalytics';
import { useTags } from '../../../common/composables/useTags';
import IconCelebration from '../../../common/resources/icons/IconCelebration.vue';
import IconGift from '../../../common/resources/icons/IconGift.vue';
import IconReward from '../../../common/resources/icons/IconReward.vue';
import IconMemberNumber from '../../../common/resources/icons/IconMemberNumber.vue';

/**
 * Card shown to members inside their first weeks of membership. Whether it
 * renders at all is decided upstream in CardLinksComponent, the same way the
 * return cards work, so the carousel can count its cards before laying out.
 *
 * Surface, chip and action rows come from the shared .help-card classes in
 * helpCard.scss; only the welcome header is styled locally.
 */
const props = withDefaults(defineProps<{
  pageName?: string;
}>(), {
  pageName: undefined,
});

const store: any = inject('store');

interface HelpLink {
  key: string;
  label: string;
  href: string;
  icon: Component;
  /** Analytics suffix: the button label in PascalCase, appended to the base tag. */
  tag: string;
}

// Destinations are defined in SACS-3766. They are rei.com pages rather than
// help center articles, so they are relative to the domain the microsite is
// served from and resolve in every environment.
const helpLinks: HelpLink[] = [
  {
    key: 'benefits',
    label: 'View member benefits',
    href: '/membership#perks',
    icon: IconGift,
    tag: 'ViewMemberBenefits',
  },
  {
    key: 'rewards',
    label: 'How rewards work',
    href: '/membership/co-op-member-reward',
    icon: IconReward,
    tag: 'HowRewardsWork',
  },
  {
    key: 'number',
    label: 'Find my member number',
    href: '/membership/lookup',
    icon: IconMemberNumber,
    tag: 'FindMyMemberNumber',
  },
];

const baseTag = useTags('getTag', 'HELP_CENTER_HELPCARD_MEMBER_WELCOME');

const handleLinkClick = (suffix: string) => {
  const page = props.pageName
    ? { pageName: props.pageName }
    : store?.getValues('pageName');

  useAnalytics('sendClickAnalytics', {
    ...page,
    linkName: `${baseTag}_${suffix}`,
  });
};

/**
* View tag for this card, alongside the section-level one the carousel fires.
*
* Sent on render rather than on entering the viewport: a card can sit off
* screen inside the carousel, and what this measures is how often the card was
* served, not how far the customer scrolled.
*/
onMounted(() => {
  useAnalytics('sendViewAnalytics', {
    pageName: `${baseTag}_visible`,
  });
});
</script>

<template>
  <article
    class="help-card help-card--member new-member-welcome"
    data-ui="new-member-welcome"
  >
    <div class="help-card__body">
      <span class="help-card__chip help-card__chip--member">New member</span>

      <div class="new-member-welcome__header">
        <div class="new-member-welcome__title-row">
          <span
            class="new-member-welcome__badge-icon"
            aria-hidden="true"
          >
            <IconCelebration />
          </span>

          <CdrText
            tag="h3"
            class="new-member-welcome__heading"
          >
            Welcome to the Co-op!
          </CdrText>
        </div>
      </div>

      <CdrText
        tag="p"
        class="new-member-welcome__intro"
      >
        Thanks for becoming an REI member.
      </CdrText>

      <CdrText
        tag="p"
        class="new-member-welcome__intro"
      >
        New members often have questions about rewards, member benefits, and
        member numbers. We're here to help.
      </CdrText>
    </div>

    <div class="help-card__section help-card__section--footer">
      <CdrText
        tag="h4"
        class="help-card__section-title"
      >
        Helpful actions
      </CdrText>

      <ul class="help-card__action-list">
        <li
          v-for="link in helpLinks"
          :key="link.key"
          class="help-card__action"
        >
          <CdrLink
            :href="link.href"
            target="_blank"
            rel="noopener noreferrer"
            class="help-card__action-link"
            :data-ui="`new-member-welcome-${link.key}`"
            @click="handleLinkClick(link.tag)"
          >
            <component
              :is="link.icon"
              class="help-card__action-icon"
            />

            <span class="help-card__action-label">{{ link.label }}</span>

            <IconCaretRight
              class="help-card__action-caret"
              inherit-color
            />
          </CdrLink>
        </li>
      </ul>
    </div>
  </article>
</template>

<style lang="scss">
@import 'src/main/style/newMemberWelcome.scss';
</style>
